/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { dumpYaml, KubernetesObject, V1Ingress, V1IngressRule, V1IngressServiceBackend, V1IngressTLS } from "@kubernetes/client-node"
import { splitAndTrimComma } from "yam-operator/util"
import { K8SResourceType, OperateFunction, PlanContext } from "yam-types"
import { Settings } from "../settings.d"
import { AccessApproachConfig } from "../types.d"


const apisixOperator: OperateFunction<AccessApproachConfig> = async (plan, diff, settings: Settings) => {
  const apisixConf = diff.currentItems.filter(x => x.apisix)
  if (apisixConf.length === 0) {
    plan.log.info(`'apisix' is not configured, operation skipped`)
    return
  }
  if (!diff.hasDiff) {
    plan.log.info(`'access' part not changed, operation skipped`)
    return
  }

  const createdServices = {} as { [key: string]: boolean }
  for (const access of apisixConf) {
    const serviceName = (access.serviceName || plan.data.workloadName) as string
    if (!createdServices[serviceName]) {
      // already created by previous item, this is not allowed
      createdServices[serviceName] = true
    } else {
      // serviceName must be unique, otherwise the port may conflicts
      plan.log.warn(`the access config will be ignored because serviceName is duplicated. host: ${JSON.stringify(access.host)}, serviceName: ${serviceName}`)
      continue
    }
    // compose ApisixRoute / ApisixTls / ApisixUpstream / ApisixPluginConfig etc.
    plan.action(async (ctx) => {
      if (!ctx.k8sClient.crdExists('ApisixRoute')) {
        ctx.log.error('custom resources definition `ApisixRoute` not found')
        return
      }
      let resources
      if (settings.downcastToIngress) {
        ctx.log.info(`downcast apisix to ingress resource`)
        resources = [composeIngress(access, plan, settings)]
      } else {
        resources = composeApisixCustomResources(access, plan, settings)
      }
      for (const r of resources) {
        ctx.mergeToYaml({
          name: r.metadata?.name + '-' + r.kind,
          content: r
        })
      }
    }, `apply-apisix-cr`)
  }
  if (diff.deletedItems.length > 0) {
    for (const access of diff.deletedItems) {
      if (access.apisix) {
        plan.log.warn(`deleted routes detected: please delete this custom resource manually:\n${dumpYaml(access.apisix)}`)
      }

    }
  }
}

function composeApisixCustomResources(access: AccessApproachConfig, plan: PlanContext, settings: Settings): KubernetesObject[] {
  const result: KubernetesObject[] = []
  if (access.apisix?.plugin_config) {
    result.push({
      kind: 'ApisixPluginConfig',
      metadata: {
        name: access.apisix.name
          ?? (
            (access.serviceName ?? plan.data.workloadName) + '-' + access.apisix.plugin_config.name
          ),
        namespace: access.apisix.namespace ?? plan.data.namespace
      },
      spec: {
        plugins: access.apisix.plugin_config.plugins
      }
    } as KubernetesObject)
  }
  const serviceName = (access.serviceName ?? plan.data.workloadName) as string
  if (access.apisix?.routes) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const workload = plan.data.currentModelFull.deploy as any
    let defaultPort = 80
    if (workload?.containers && workload?.containers.length > 0) {
      if (workload?.containers[0].port) {
        defaultPort = parseInt(workload?.containers[0].port as string)
      } else if (workload?.containers[0].ports) {
        defaultPort = workload?.containers[0].ports[0].containerPort
      }
    }
    const defaultBackend = {
      serviceName,
      servicePort: access.servicePort as number || defaultPort
    }
    const defaultHosts = access.host ? (splitAndTrimComma(access.host as string)) as [string, ...string[]] : undefined
    const defaultPluginName = access.apisix.plugin_config?.name
    const http = access.apisix.routes.http
    const stream = access.apisix.routes.stream
    const tls = access.apisix.tls

    if (http) {
      http.forEach(h => {
        if (!h.backends) {
          h.backends = [defaultBackend]
        }
        if (!h.match.hosts) {
          h.match.hosts = defaultHosts
        }
        if (!h.plugins) {
          h.plugins = []
        }
        if (settings.defaultPlugins) {
          h.plugins.unshift(...settings.defaultPlugins)
        }
        if (defaultPluginName && !h.plugin_config_name) {
          h.plugin_config_name = defaultPluginName
        }
      })
    }
    if (stream) {
      stream.forEach(s => {
        if (!s.backend) {
          s.backend = defaultBackend
        }
      })
    }
    if (!tls && access.cert && defaultHosts) {
      access.apisix.tls = {
        hosts: defaultHosts,
        secret: {
          name: access.cert as string,
          namespace: plan.data.namespace
        }
      }
    }
    result.push({
      kind: 'ApisixRoute',
      metadata: {
        name: access.apisix.name ?? serviceName,
        namespace: access.apisix.namespace ?? plan.data.namespace,
        annotations: Object.assign({}, settings.apisixRouteAnnotations, access.apisix.routes.annotations)
      },
      spec: access.apisix.routes
    } as KubernetesObject)
  }
  if (access.apisix?.tls) {
    result.push({
      kind: 'ApisixTls',
      metadata: {
        name: access.apisix.name ?? serviceName,
        namespace: access.apisix.namespace ?? plan.data.namespace
      },
      spec: access.apisix.tls
    } as KubernetesObject)
  }
  if (access.apisix?.upstream) {
    result.push({
      kind: 'ApisixUpstream',
      metadata: {
        name: access.apisix.name ?? serviceName,
        namespace: access.apisix.namespace ?? plan.data.namespace
      },
      spec: access.apisix.upstream
    } as KubernetesObject)
  }
  return result
}

function composeIngress(access: AccessApproachConfig, plan: PlanContext, settings: Settings): V1Ingress {
  const serviceName = (access.serviceName ?? plan.data.workloadName) as string
  const ingress: V1Ingress = {
    apiVersion: "networking.k8s.io/v1",
    kind: K8SResourceType.Ingress,
    metadata: {
      name: serviceName,
      annotations: access.apisix?.routes?.annotations,
      labels: { workload: plan.data.workloadName }
    },
    spec: {}
  }
  ingress.spec!.ingressClassName = settings.downcastIngressClassName

  if (access.apisix?.routes) {
    const routes = access.apisix?.routes
    const rules = [] as V1IngressRule[]
    let defaultPort = 80
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const workload = plan.data.currentModelFull.deploy as any
    if (workload?.containers && workload?.containers.length > 0) {
      if (workload?.containers[0].port) {
        defaultPort = parseInt(workload?.containers[0].port as string)
      } else if (workload?.containers[0].ports) {
        defaultPort = workload?.containers[0].ports[0].containerPort
      }
    }
    const defaultBackend: V1IngressServiceBackend = {
      name: serviceName,
      port: {
        number: access.servicePort as number || defaultPort
      }
    }
    routes.http?.forEach(h => {
      for (const host of h.match.hosts ?? []) {
        rules.push({
          host,
          http: {
            paths: h.match.paths.map(path => {
              let p = path
              let pt = 'Exact'

              if (p.endsWith("*")) {
                pt = 'Prefix'
                p = p.substring(0, p.length - 1)
              }
              return {
                backend: {
                  service: defaultBackend
                },
                path: p,
                pathType: pt,
              }
            })
          }
        })
      }
    })
    ingress.spec!.rules = rules
  }

  if (access.apisix?.tls) {
    const tls = {} as V1IngressTLS
    const apisixTls = access.apisix?.tls
    tls.hosts = apisixTls.hosts
    tls.secretName = apisixTls.secret.name
    ingress.spec!.tls = [tls]
  }

  return ingress
}

export default apisixOperator
